import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { useRazorpay } from 'react-razorpay';
import { ChevronDown, Calendar, MapPin, Dumbbell, Apple, Activity, CheckCircle, Upload } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function App() {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [activeTab, setActiveTab] = useState('2025');
  const [formData, setFormData] = useState({
    fullName: '', email: '', phone: '', city: '', organisation: '', role: 'Trainer', tier: 'Standard', workshop: 'Both'
  });
  const [subscriberEmail, setSubscriberEmail] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const { error, isLoading, Razorpay } = useRazorpay();

  useEffect(() => {
    const eventDate = new Date('2026-06-12T09:00:00');
    const interval = setInterval(() => {
      const now = new Date();
      const difference = eventDate - now;
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subscriberEmail) return;
    try {
      const res = await fetch(`${API_URL}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: subscriberEmail })
      });
      if (res.ok) {
        setStatusMsg('Subscribed successfully!');
        setSubscriberEmail('');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const prices = { 'Early Bird': 199900, 'Standard': 299900, 'VIP': 499900 };
      const amount = prices[formData.tier] || 299900;

      const initRes = await fetch(`${API_URL}/api/register/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, amount })
      });
      const order = await initRes.json();

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_mock',
        amount: order.amount,
        currency: order.currency,
        name: "FitCraft 3.0",
        description: `${formData.tier} Registration`,
        order_id: order.id,
        handler: async (response) => {
          const verifyRes = await fetch(`${API_URL}/api/register/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              registration_data: formData
            })
          });
          if (verifyRes.ok) {
            alert('Registration Successful! Welcome to FitCraft 3.0');
          } else {
            alert('Payment verification failed.');
          }
        },
        prefill: { name: formData.fullName, email: formData.email, contact: formData.phone },
        theme: { color: "#6E38D5" } // Blurple
      };

      const rzp1 = new Razorpay(options);
      rzp1.open();
    } catch (err) {
      console.error(err);
      alert('Error initiating payment');
    }
  };

  return (
    <div className="min-h-screen bg-violet font-body text-white selection:bg-aqua selection:text-violet">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-violet/90 backdrop-blur-xl border-b border-blurple shadow-lg py-4">
        <div className="container mx-auto px-6 sm:px-12 lg:px-24 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="inline-flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_fitcraft-training/artifacts/4jhoscqy_AF-Primary-Logo-white.png" 
                  alt="Anytime Fitness" 
                  className="h-10 object-contain"
                />
            </div>
          </div>
          <nav className="hidden md:flex gap-8 text-sm font-bold uppercase tracking-wide">
            <a href="#about" className="hover:text-aqua transition-colors">About</a>
            <a href="#past-editions" className="hover:text-aqua transition-colors">Past Editions</a>
            <a href="#speakers" className="hover:text-aqua transition-colors">Speakers</a>
            <a href="#faq" className="hover:text-aqua transition-colors">FAQ</a>
          </nav>
          <a href="#register" className="bg-blurple text-white font-bold uppercase tracking-wider py-3 px-8 rounded-full hover:bg-aqua hover:text-violet shadow-[0_0_15px_rgba(110,56,213,0.5)] hover:shadow-[0_0_20px_rgba(46,215,222,0.8)] transition-all duration-300 text-sm">
            Register Now
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-24 overflow-hidden bg-gradient-violet-blurple">
        {/* Background Patterns */}
        <div className="absolute inset-0 z-0 pattern-purple-aqua"></div>
        <div className="absolute bottom-0 w-full h-1/2 bg-gradient-to-t from-violet to-transparent z-0"></div>

        <div className="container mx-auto px-6 sm:px-12 lg:px-24 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex justify-center mb-6 mt-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_fitcraft-training/artifacts/831jfgei_LOGO.jpg-removebg-preview.png" 
                alt="FitCraft 3.0" 
                className="h-20 sm:h-28 md:h-36 object-contain hover:scale-105 transition-transform duration-300"
              />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-widest uppercase mb-6 text-white drop-shadow-lg">
              India's Biggest <br /> Fitness Mastery Event
            </h1>
            <p className="text-xl sm:text-2xl font-bold uppercase tracking-widest text-aqua mb-8 font-heading max-w-4xl mx-auto">
              Elevate Client Training. Master Nutrition. Optimize Recovery.
            </p>
            
            <div className="flex flex-wrap justify-center gap-6 mb-12 text-sm sm:text-base font-semibold">
              <div className="flex items-center gap-2 bg-blurple/50 backdrop-blur-md px-6 py-3 rounded-full border border-aqua/30">
                <Calendar className="text-orange" size={20} />
                12th June 2026
              </div>
              <div className="flex items-center gap-2 bg-blurple/50 backdrop-blur-md px-6 py-3 rounded-full border border-aqua/30">
                <MapPin className="text-aqua" size={20} />
                Bharat Mandapam, New Delhi
              </div>
            </div>

            {/* Countdown */}
            <div className="flex justify-center gap-4 sm:gap-6 mb-12" data-testid="countdown-timer">
              {[
                { label: 'Days', value: timeLeft.days },
                { label: 'Hours', value: timeLeft.hours },
                { label: 'Mins', value: timeLeft.minutes },
                { label: 'Secs', value: timeLeft.seconds }
              ].map((item, idx) => (
                <div key={idx} className="bg-white/10 backdrop-blur-xl border border-white/20 shadow-xl rounded-2xl p-4 sm:p-6 flex flex-col items-center min-w-[80px] sm:min-w-[100px]">
                  <span className="text-4xl sm:text-5xl font-black font-heading text-white">{item.value}</span>
                  <span className="text-xs sm:text-sm font-bold uppercase tracking-widest text-aqua mt-2">{item.label}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="#register" className="bg-blurple text-white font-black uppercase tracking-wider py-4 px-10 rounded-full shadow-[0_0_20px_rgba(110,56,213,0.6)] hover:bg-aqua hover:text-violet hover:shadow-[0_0_30px_rgba(46,215,222,0.8)] hover:-translate-y-1 transition-all duration-300 text-lg">
                Register Now
              </a>
              <a href="#speakers" className="bg-transparent border-2 border-aqua text-aqua hover:bg-aqua hover:text-violet font-black uppercase tracking-wider py-4 px-10 rounded-full transition-all duration-300 text-lg">
                Join Waitlist
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About FitCraft */}
      <section id="about" className="py-24 sm:py-32 relative bg-violet border-y border-blurple/30">
        <div className="absolute inset-0 z-0 pattern-purple-orange opacity-50"></div>
        <div className="container mx-auto px-6 sm:px-12 lg:px-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <h2 className="text-4xl sm:text-5xl font-black tracking-tight uppercase font-heading text-white">
                About <span className="text-aqua">FitCraft</span>
              </h2>
              <div className="w-24 h-2 bg-orange"></div>
              <p className="text-lg leading-relaxed text-white font-secondary">
                FitCraft is India's premier fitness industry event powered by AF Aspire Academy. We bring together trainers, sales professionals, gym owners, and industry leaders to shape the future of fitness.
              </p>
              <p className="text-lg leading-relaxed text-white font-secondary">
                Now in its 3rd edition, FitCraft builds on two transformative years to deliver unparalleled education, networking, and inspiration.
              </p>
              
              <div className="grid grid-cols-2 gap-6 pt-8">
                <div className="bg-blurple border border-aqua/30 rounded-2xl p-6 shadow-lg">
                  <div className="text-4xl font-black text-white font-heading mb-2">500+</div>
                  <div className="text-sm font-bold uppercase tracking-wider text-aqua">Attendees</div>
                </div>
                <div className="bg-blurple border border-orange/30 rounded-2xl p-6 shadow-lg">
                  <div className="text-4xl font-black text-white font-heading mb-2">10+</div>
                  <div className="text-sm font-bold uppercase tracking-wider text-orange">Power Sessions</div>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[4/5] rounded-3xl overflow-hidden relative shadow-2xl shadow-blurple/50 border-4 border-white/10">
                <img 
                  src="https://images.unsplash.com/photo-1679500502523-b40fb6f0563d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxmaXRuZXNzJTIwdHJhaW5lciUyMGV2ZW50fGVufDB8fHx8MTc3NTAxOTgxMXww&ixlib=rb-4.1.0&q=85" 
                  alt="FitCraft Event" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-violet-blurple mix-blend-overlay opacity-60"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Past Editions (FOMO) */}
      <section id="past-editions" className="py-24 sm:py-32 bg-blurple relative">
        <div className="container mx-auto px-6 sm:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight uppercase font-heading mb-6 text-white">
              The <span className="text-orange">Legacy</span>
            </h2>
            <div className="w-24 h-2 bg-aqua mx-auto mb-8"></div>
            <div className="flex justify-center gap-4">
              <button 
                onClick={() => setActiveTab('2024')}
                className={`px-8 py-3 rounded-full font-black uppercase tracking-wider transition-all ${activeTab === '2024' ? 'bg-white text-blurple shadow-lg' : 'bg-violet text-white hover:bg-violet/80 border border-white/20'}`}
              >
                FitCraft 1.0 (2024)
              </button>
              <button 
                onClick={() => setActiveTab('2025')}
                className={`px-8 py-3 rounded-full font-black uppercase tracking-wider transition-all ${activeTab === '2025' ? 'bg-aqua text-violet shadow-lg' : 'bg-violet text-white hover:bg-violet/80 border border-white/20'}`}
              >
                FitCraft 2.0 (2025)
              </button>
            </div>
          </div>

          <motion.div 
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {activeTab === '2025' && (
              <div className="space-y-12">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-violet border border-aqua/30 rounded-2xl p-8 shadow-xl">
                    <h4 className="text-xl font-black text-aqua mb-2 uppercase">22nd July 2025</h4>
                    <p className="text-white font-secondary">Bharat Mandapam</p>
                  </div>
                  <div className="bg-violet border border-orange/30 rounded-2xl p-8 shadow-xl">
                    <h4 className="text-xl font-black text-orange mb-2 uppercase">Two Tracks</h4>
                    <p className="text-white font-secondary">Trainers & Sales Workshops</p>
                  </div>
                  <div className="bg-violet border border-medium_purple/30 rounded-2xl p-8 shadow-xl">
                    <h4 className="text-xl font-black text-medium_purple mb-2 uppercase">Gratification</h4>
                    <p className="text-white font-secondary">Honouring 3+ and 5+ year team members</p>
                  </div>
                </div>

                {/* Gallery */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="masonry-gallery">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className={`rounded-xl overflow-hidden relative group border-4 border-violet ${i === 1 || i === 4 ? 'col-span-2 row-span-2' : ''}`}>
                      <img 
                        src="https://images.unsplash.com/photo-1586173463725-1de44ae880d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxmaXRuZXNzJTIwbWFzdGVyY2xhc3MlMjBldmVudCUyMHN0YWdlfGVufDB8fHx8MTc3NTAxOTgzMXww&ixlib=rb-4.1.0&q=85" 
                        alt="Event" 
                        className="w-full h-full object-cover min-h-[200px] group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-violet/70 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-6 text-center">
                        <p className="font-bold text-lg mb-2 italic">"Life changing sessions!"</p>
                        <span className="font-black uppercase text-sm border-2 border-aqua text-aqua px-4 py-2 rounded-full bg-violet/80">
                          [INSERT EVENT PHOTO]
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {activeTab === '2024' && (
              <div className="text-center py-20 bg-violet rounded-3xl border border-white/10">
                <h3 className="text-2xl font-black text-white/50 uppercase">FitCraft 1.0 Gallery & Insights</h3>
                <p className="text-white/40 mt-2 font-secondary">The beginning of the revolution.</p>
              </div>
            )}
          </motion.div>

          <div className="mt-24 bg-violet w-full p-8 rounded-3xl border-2 border-orange/50 text-center shadow-[0_0_30px_rgba(255,117,0,0.3)]">
            <div className="text-aqua font-bold tracking-widest uppercase mb-4 text-sm">500+ Attendees | 2 Editions | 10+ Power Sessions | 1 Unforgettable Day</div>
            <h3 className="text-3xl sm:text-5xl font-black font-heading text-white">
              "You missed it once. Don't miss it again."
            </h3>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-24 sm:py-32 relative bg-violet border-t border-blurple">
        <div className="absolute inset-0 z-0 pattern-purple-aqua opacity-30"></div>
        <div className="container mx-auto px-6 sm:px-12 lg:px-24 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight uppercase font-heading mb-6 text-white flex flex-col items-center justify-center gap-6">
              <span>What to Expect at</span>
              <img 
                src="https://customer-assets.emergentagent.com/job_fitcraft-training/artifacts/831jfgei_LOGO.jpg-removebg-preview.png" 
                alt="FitCraft 3.0" 
                className="h-20 sm:h-28 md:h-32 object-contain"
              />
            </h2>
            <div className="w-24 h-2 bg-aqua mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-blurple border-2 border-blurple/50 rounded-3xl p-10 hover:-translate-y-2 transition-transform duration-300 relative overflow-hidden group shadow-2xl">
              <Dumbbell className="text-white w-14 h-14 mb-6" />
              <h3 className="text-2xl font-black uppercase mb-4 text-white">Training</h3>
              <p className="text-white/90 font-secondary leading-relaxed">Expert-led sessions, live demos, and cutting-edge techniques to elevate your coaching game.</p>
            </div>
            
            <div className="bg-aqua border-2 border-aqua/50 rounded-3xl p-10 hover:-translate-y-2 transition-transform duration-300 relative overflow-hidden group shadow-2xl">
              <Apple className="text-violet w-14 h-14 mb-6" />
              <h3 className="text-2xl font-black uppercase mb-4 text-violet">Nutrition</h3>
              <p className="text-violet/90 font-secondary font-semibold leading-relaxed">Science-backed performance nutrition, practical guidance, and modern dietary strategies.</p>
            </div>

            <div className="bg-medium_purple border-2 border-medium_purple/50 rounded-3xl p-10 hover:-translate-y-2 transition-transform duration-300 relative overflow-hidden group shadow-2xl">
              <Activity className="text-white w-14 h-14 mb-6" />
              <h3 className="text-2xl font-black uppercase mb-4 text-white">Recovery</h3>
              <p className="text-white/90 font-secondary leading-relaxed">Tools, modalities, and recovery mindset to ensure longevity and sustained performance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Speakers Teaser */}
      <section id="speakers" className="py-24 sm:py-32 bg-gradient-violet-blurple relative border-t border-aqua/30">
        <div className="container mx-auto px-6 sm:px-12 lg:px-24 text-center">
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight uppercase font-heading mb-6 text-white">
            India's most exciting fitness lineup is being curated.
          </h2>
          <p className="text-xl text-orange font-bold uppercase tracking-wider mb-16">Stay Tuned.</p>

          <div className="flex flex-wrap justify-center gap-8 mb-16">
            {[1, 2, 3].map((i) => (
              <div key={i} className="w-64 aspect-[3/4] rounded-2xl overflow-hidden relative border-4 border-blurple shadow-[0_0_20px_rgba(110,56,213,0.5)]">
                <img 
                  src="https://images.pexels.com/photos/29180747/pexels-photo-29180747.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                  alt="Speaker TBA" 
                  className="w-full h-full object-cover blur-xl opacity-50 grayscale"
                />
                <div className="absolute inset-0 bg-violet/60 mix-blend-multiply"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-black uppercase tracking-widest text-white bg-blurple px-6 py-2 rounded-full border border-aqua/50 shadow-lg">
                    Speaker TBA
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="max-w-md mx-auto bg-violet p-8 rounded-3xl border border-white/20 shadow-2xl">
            <h4 className="text-lg font-black uppercase mb-4 text-aqua">Notify me when speakers are announced</h4>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3" data-testid="subscribe-form">
              <input 
                type="email" 
                placeholder="Enter your email" 
                required
                value={subscriberEmail}
                onChange={(e) => setSubscriberEmail(e.target.value)}
                className="flex-1 bg-white/10 border border-white/30 rounded-full px-6 py-3 font-secondary text-white focus:outline-none focus:border-aqua focus:bg-white/20 transition-colors"
                data-testid="subscribe-email-input"
              />
              <button 
                type="submit" 
                className="bg-aqua text-violet font-black uppercase tracking-wide px-8 py-3 rounded-full hover:bg-white transition-colors"
                data-testid="subscribe-button"
              >
                Notify
              </button>
            </form>
            {statusMsg && <p className="text-aqua font-bold mt-4 text-sm">{statusMsg}</p>}
          </div>
        </div>
      </section>

      {/* Brand Partners Marquee */}
      <section className="py-12 bg-white border-y border-blurple/30">
        <Marquee gradient={false} speed={40}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="mx-16 flex items-center gap-4">
              <div className="text-violet font-black text-2xl uppercase tracking-widest font-heading opacity-50 hover:opacity-100 transition-opacity">
                BRAND PARTNER {i}
              </div>
            </div>
          ))}
        </Marquee>
      </section>

      {/* Registration Section */}
      <section id="register" className="py-24 sm:py-32 relative bg-violet border-y border-blurple">
        <div className="absolute inset-0 z-0 pattern-purple-orange opacity-40"></div>
        <div className="container mx-auto px-6 sm:px-12 lg:px-24 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight uppercase font-heading mb-4 text-white">
              Secure Your <span className="text-orange">Spot</span>
            </h2>
            <div className="w-24 h-2 bg-orange mx-auto mb-6"></div>
            <p className="text-xl text-white font-secondary">Limited seats available. Register now to avoid FOMO.</p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Form */}
            <div className="bg-blurple border-4 border-white/10 rounded-3xl p-8 sm:p-12 shadow-[0_0_40px_rgba(110,56,213,0.3)]">
              <h3 className="text-2xl font-black uppercase mb-8 border-b border-white/20 pb-4 text-white">Attendee Details</h3>
              <form onSubmit={handleRegister} className="space-y-6" data-testid="registration-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold mb-2 text-white uppercase tracking-wide">Full Name</label>
                    <input 
                      type="text" 
                      required
                      value={formData.fullName}
                      onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                      className="w-full bg-violet border border-white/20 rounded-xl px-4 py-3 text-white font-secondary focus:outline-none focus:border-aqua transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2 text-white uppercase tracking-wide">Email Address</label>
                    <input 
                      type="email" 
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full bg-violet border border-white/20 rounded-xl px-4 py-3 text-white font-secondary focus:outline-none focus:border-aqua transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2 text-white uppercase tracking-wide">Phone Number</label>
                    <input 
                      type="tel" 
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full bg-violet border border-white/20 rounded-xl px-4 py-3 text-white font-secondary focus:outline-none focus:border-aqua transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2 text-white uppercase tracking-wide">City</label>
                    <input 
                      type="text" 
                      required
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      className="w-full bg-violet border border-white/20 rounded-xl px-4 py-3 text-white font-secondary focus:outline-none focus:border-aqua transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2 text-white uppercase tracking-wide">Organisation / Gym Name</label>
                    <input 
                      type="text" 
                      required
                      value={formData.organisation}
                      onChange={(e) => setFormData({...formData, organisation: e.target.value})}
                      className="w-full bg-violet border border-white/20 rounded-xl px-4 py-3 text-white font-secondary focus:outline-none focus:border-aqua transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2 text-white uppercase tracking-wide">Role</label>
                    <select 
                      value={formData.role}
                      onChange={(e) => setFormData({...formData, role: e.target.value})}
                      className="w-full bg-violet border border-white/20 rounded-xl px-4 py-3 text-white font-secondary focus:outline-none focus:border-aqua transition-colors"
                    >
                      <option value="Trainer">Trainer</option>
                      <option value="Sales">Sales</option>
                      <option value="Gym Owner">Gym Owner</option>
                      <option value="Franchisee">Franchisee</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="pt-8 border-t border-white/20">
                  <label className="block text-sm font-bold mb-4 text-white uppercase tracking-wide">Select Ticket Tier</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {['Early Bird', 'Standard', 'VIP'].map((tier) => (
                      <div 
                        key={tier}
                        onClick={() => setFormData({...formData, tier})}
                        className={`cursor-pointer border-4 rounded-xl p-4 text-center transition-all shadow-md ${formData.tier === tier ? 'border-aqua bg-violet' : 'border-white/20 hover:border-aqua/50 bg-violet/50'}`}
                      >
                        <div className={`font-black uppercase mb-2 ${formData.tier === tier ? 'text-aqua' : 'text-white'}`}>{tier}</div>
                        <div className="text-sm font-bold text-white/80">
                          {tier === 'Early Bird' ? '₹1,999' : tier === 'Standard' ? '₹2,999' : '₹4,999'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-8 border-t border-white/20">
                  <label className="block text-sm font-bold mb-4 text-white uppercase tracking-wide">Workshop Preference</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {['Trainers Workshop', 'Sales Workshop', 'Both'].map((ws) => (
                      <div 
                        key={ws}
                        onClick={() => setFormData({...formData, workshop: ws})}
                        className={`cursor-pointer border-4 rounded-xl p-4 text-center transition-all shadow-md ${formData.workshop === ws ? 'border-orange bg-violet' : 'border-white/20 hover:border-orange/50 bg-violet/50'}`}
                      >
                        <div className={`font-black uppercase text-sm ${formData.workshop === ws ? 'text-orange' : 'text-white'}`}>{ws}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <button 
                  type="submit" 
                  className="w-full mt-10 bg-aqua text-violet font-black uppercase tracking-widest py-5 rounded-xl shadow-[0_0_20px_rgba(46,215,222,0.4)] hover:bg-white hover:shadow-[0_0_30px_rgba(255,255,255,0.6)] hover:-translate-y-1 transition-all duration-300 text-xl"
                  data-testid="submit-registration-btn"
                >
                  Proceed to Payment
                </button>
                <div className="flex items-center justify-center gap-2 mt-4 text-sm text-white font-bold tracking-widest uppercase">
                  <CheckCircle size={18} className="text-aqua" /> Secure Payment via Razorpay
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 sm:py-32 relative bg-blurple">
        <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight uppercase font-heading text-white mb-4">
              Got <span className="text-aqua">Questions?</span>
            </h2>
            <div className="w-24 h-2 bg-aqua mx-auto"></div>
          </div>

          <Accordion type="single" collapsible className="w-full bg-violet rounded-3xl p-8 shadow-2xl border border-white/10" data-testid="faq-accordion">
            {[
              { q: "Who should attend?", a: "FitCraft 3.0 is designed for Anytime Fitness trainers, sales professionals, gym owners, and franchisees looking to elevate their skills and business." },
              { q: "What's included in the ticket?", a: "Access to all keynotes, chosen workshop track, networking lunch, and entry to the Gratification Ceremony." },
              { q: "Can I attend both workshops?", a: "Yes, you can select 'Both' during registration. Sessions are structured to allow cross-attendance for key segments." },
              { q: "What is the Gratification Ceremony?", a: "A special awards evening honouring the dedication of our 3+ and 5+ year Anytime Fitness India team members." },
              { q: "Is there parking at Bharat Mandapam?", a: "Yes, ample parking is available at the venue for all attendees." },
              { q: "How do I access the FitCraft WhatsApp group?", a: "Upon successful payment, you will receive an automated confirmation email containing the exclusive WhatsApp group link." }
            ].map((faq, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`} className="border-b-2 border-white/10 px-4 last:border-0">
                <AccordionTrigger className="text-left font-black text-xl hover:no-underline hover:text-aqua transition-colors py-6 uppercase tracking-wide">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-white/80 font-secondary text-lg leading-relaxed pb-6">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-violet py-16 border-t-8 border-aqua">
        <div className="container mx-auto px-6 sm:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8 mb-12">
            <div className="inline-flex">
              <img 
                src="https://customer-assets.emergentagent.com/job_fitcraft-training/artifacts/4jhoscqy_AF-Primary-Logo-white.png" 
                alt="Anytime Fitness" 
                className="h-12 object-contain"
              />
            </div>
            <div className="flex gap-6 text-sm font-bold uppercase tracking-wider text-white">
              <a href="#" className="hover:text-aqua transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-aqua transition-colors">Refund Policy</a>
              <a href="#" className="hover:text-aqua transition-colors">Terms & Conditions</a>
            </div>
          </div>
          <div className="text-center text-white/70 text-sm font-secondary border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="font-bold">© Anytime Fitness Franchisor, LLC 2026. All rights reserved.</p>
            <p className="font-bold">Contact: <a href="mailto:damini.sharma@anytimefitness.in" className="text-aqua hover:text-white transition-colors">damini.sharma@anytimefitness.in</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
